import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { saveRemoteFile } from '@/utils/saveFile';
import { Box, Text, Flex, Button } from '@chakra-ui/react';
import { X, ChevronDown, Plus, Minus, FileUp, Trash2, Clock, Download, ShieldCheck, ShieldOff, Check } from 'lucide-react';
import { EventImage } from '@/components/common/EventImage';
import { supabase } from '@/lib/supabase';
import { insertVolunteerNotification } from '@/utils/volunteerNotificationInsert';
import { uploadImageToStorage } from '@/pages/Organizations/components/shared/utils/eventHelpers';
import { ConfirmModal } from '@/components/ui/ConfirmModal/ConfirmModal';
import { DurationStepper } from '@/components/ui/DurationStepper';
import type { OptimizedVolunteer } from '@/utils/optimizedQueries';
import type { VolunteersEvent } from './useVolunteersPage';
import type { OrgMemberManagePayload } from './fetchOrgMemberManageContext';
import EventPreviewModal, { type ProfileEvent } from '@/components/common/EventPreviewModal';
import { MAX_CERTIFICATE_BYTES, MAX_CERTIFICATE_LABEL } from '@/constants/uploadLimits';
import { ShiftList } from '@/components/common/ShiftList';
import { useEventShifts } from '@/hooks/useEventShifts';
import { assignVolunteerShifts, fetchVolunteerShiftIds, removeVolunteerShift } from '@/utils/eventShiftsApi';
import {
  MINUTE_STEP,
  addToTotal,
  formatDuration,
  hoursToMinutes,
  minutesToHours,
  validateDuration,
} from '@/utils/volunteerHours';

type CertificateItem = { id: string; title: string; image_url: string; date: string };
type TaskAssignmentRow = {
  id: string;
  assigned_volunteers: string[] | null;
  assigned_departments: string[] | null;
};

interface Props {
  open: boolean;
  onClose: () => void;
  volunteer: OptimizedVolunteer | null;
  event: VolunteersEvent | null;
  onSaved: () => void;
  onError?: (message: string) => void;
  /** When set: hide department/shifts, show org events list and org-level remove. */
  orgMemberManage?: OrgMemberManagePayload | null;
  /** Event department list for move-to-department (departments view). */
  moveEventDepartments?: string[];
  departmentColors?: Record<string, string>;
  onUpdateVolunteerDepartment?: (volunteerId: string, department: string | null) => Promise<void>;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text fontSize="11px" fontWeight="semibold" color="volyx.textSecondary" textTransform="uppercase" letterSpacing="wider" m="0" mb="2">
      {children}
    </Text>
  );
}

async function cleanupVolunteerTasksForRemoval(eventId: string, volunteerUserId: string) {
  const { data: taskRows, error: fetchError } = await supabase
    .from('tasks')
    .select('id, assigned_volunteers, assigned_departments')
    .eq('event_id', eventId)
    .contains('assigned_volunteers', [volunteerUserId]);

  if (fetchError) {
    throw new Error(fetchError.message || 'Failed to update volunteer tasks');
  }

  const tasks = (taskRows ?? []) as TaskAssignmentRow[];
  if (tasks.length === 0) return;

  for (const task of tasks) {
    const nextVolunteers = (Array.isArray(task.assigned_volunteers) ? task.assigned_volunteers : [])
      .filter((id) => id && id !== volunteerUserId);
    const departments = (Array.isArray(task.assigned_departments) ? task.assigned_departments : [])
      .map((department) => department?.trim())
      .filter((department): department is string => Boolean(department));

    if (nextVolunteers.length === 0 && departments.length === 0) {
      const { error: deleteError } = await supabase.from('tasks').delete().eq('id', task.id);
      if (deleteError) {
        throw new Error(deleteError.message || 'Failed to remove volunteer tasks');
      }
      continue;
    }

    const { error: updateError } = await supabase
      .from('tasks')
      .update({ assigned_volunteers: nextVolunteers })
      .eq('id', task.id);
    if (updateError) {
      throw new Error(updateError.message || 'Failed to update volunteer tasks');
    }
  }
}

async function removeVolunteerEventMembership({
  volunteerId,
  volunteerUserId,
  eventId,
}: {
  volunteerId: string;
  volunteerUserId: string;
  eventId: string;
}) {
  await cleanupVolunteerTasksForRemoval(eventId, volunteerUserId);

  const { error: updateErr } = await supabase
    .from('volunteers')
    .update({ is_active: false })
    .eq('id', volunteerId);
  if (updateErr) {
    throw new Error(updateErr.message || 'Failed to remove volunteer');
  }

  const { data: chatData, error: chatError } = await supabase
    .from('chats')
    .select('id')
    .eq('event_id', eventId)
    .maybeSingle();
  if (chatError) {
    throw new Error(chatError.message || 'Failed to remove volunteer from event chat');
  }

  if (chatData?.id) {
    const { error: participantError } = await supabase
      .from('chat_participants')
      .delete()
      .eq('chat_id', chatData.id)
      .eq('user_id', volunteerUserId);
    if (participantError) {
      throw new Error(participantError.message || 'Failed to remove volunteer from event chat');
    }
  }
}

export function VolunteersManageModal({
  open,
  onClose,
  volunteer,
  event,
  onSaved,
  onError,
  orgMemberManage = null,
  moveEventDepartments,
  departmentColors,
  onUpdateVolunteerDepartment,
}: Props) {
  const isOrgMemberManage = Boolean(orgMemberManage);
  const eventDepartments = useMemo(() => event?.departments ?? [], [event?.departments]);

  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [assignedShiftIds, setAssignedShiftIds] = useState<string[]>([]);
  const [updatingShift, setUpdatingShift] = useState(false);
  const [showDeptDropdown, setShowDeptDropdown] = useState(false);
  const [movingDepartment, setMovingDepartment] = useState(false);
  const [previewEvent, setPreviewEvent] = useState<ProfileEvent | null>(null);
  const [showEventPreview, setShowEventPreview] = useState(false);

  const volunteerId = volunteer?.id;

  const currentMoveDepartment = useMemo(() => {
    const depts = volunteer?.departments ?? [];
    if (depts.length === 0) return null;
    const first = depts[0];
    return typeof first === 'string' ? first.split(',')[0]?.trim() || null : null;
  }, [volunteer?.departments]);

  const effectiveCurrentDepartment = useMemo(() => {
    if (selectedDepartments.length > 0) return selectedDepartments[0] ?? null;
    return currentMoveDepartment;
  }, [selectedDepartments, currentMoveDepartment]);

  const moveDepartmentOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: string[] = [];
    for (const dept of moveEventDepartments ?? eventDepartments) {
      const trimmed = dept.trim();
      if (!trimmed || seen.has(trimmed)) continue;
      seen.add(trimmed);
      options.push(trimmed);
    }
    return options.sort((a, b) => a.localeCompare(b));
  }, [moveEventDepartments, eventDepartments]);

  const handleMoveToDepartment = useCallback(
    async (department: string | null) => {
      if (!onUpdateVolunteerDepartment || !volunteerId || movingDepartment) return;

      const alreadyThere = department
        ? effectiveCurrentDepartment === department
        : effectiveCurrentDepartment === null;
      if (alreadyThere) return;

      setMovingDepartment(true);
      try {
        await onUpdateVolunteerDepartment(volunteerId, department);
        setSelectedDepartments(department ? [department] : []);
        setShowDeptDropdown(false);
        onSaved();
      } catch (err) {
        onError?.(err instanceof Error ? err.message : 'Failed to move volunteer');
      } finally {
        setMovingDepartment(false);
      }
    },
    [
      onUpdateVolunteerDepartment,
      volunteerId,
      movingDepartment,
      effectiveCurrentDepartment,
      onSaved,
      onError,
    ],
  );

  const currentHours = volunteer?.hours ?? 0;
  const currentMinutes = hoursToMinutes(currentHours);
  const totalHoursAllOrgEvents = orgMemberManage?.totalHoursSum ?? 0;
  const volunteerUserId = volunteer?.volunteer_id;
  const eventId = volunteer?.event_id;

  const shiftsEnabled = open && !isOrgMemberManage;
  const { shifts: eventShifts, refetch: refetchShifts } = useEventShifts(eventId, shiftsEnabled);

  const loadAssignedShifts = useCallback(async () => {
    if (!eventId || !volunteerUserId) {
      setAssignedShiftIds([]);
      return;
    }
    const ids = await fetchVolunteerShiftIds(eventId, volunteerUserId);
    if (ids === null) {
      onError?.('Could not load this volunteer\'s shifts');
      return;
    }
    setAssignedShiftIds(ids);
    // onError is recreated by the parent on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, volunteerUserId]);

  useEffect(() => {
    if (!shiftsEnabled) {
      setAssignedShiftIds([]);
      return;
    }
    void loadAssignedShifts();
  }, [shiftsEnabled, loadAssignedShifts]);

  const handleShiftToggle = useCallback(
    async (shiftId: string) => {
      if (!eventId || !volunteerUserId || updatingShift) return;
      setUpdatingShift(true);
      try {
        if (assignedShiftIds.includes(shiftId)) {
          const result = await removeVolunteerShift(shiftId, volunteerUserId);
          if (!result.ok) onError?.(result.error);
        } else {
          // Capacity is checked in the database, so a shift that filled up since the list
          // loaded comes back as full instead of being overfilled.
          const result = await assignVolunteerShifts(eventId, volunteerUserId, [shiftId]);
          if (!result.ok) onError?.(result.error);
          else if (result.full.length > 0) onError?.('This shift is full.');
        }
      } finally {
        await Promise.all([loadAssignedShifts(), refetchShifts()]);
        setUpdatingShift(false);
      }
    },
    [eventId, volunteerUserId, updatingShift, assignedShiftIds, onError, loadAssignedShifts, refetchShifts],
  );

  const profile = volunteer?.profiles as { first_name?: string; last_name?: string; profile_image?: string } | undefined;
  const displayName =
    profile
      ? `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim() || 'Volunteer'
      : 'Volunteer';
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
  const assignedOrgEventCount = orgMemberManage?.eventsIn.length ?? 0;
  const removeFromOrganizationMessage = isOrgMemberManage
    ? assignedOrgEventCount > 0
      ? `Remove ${displayName} from your organization? This will also remove them from all ${assignedOrgEventCount} organization ${assignedOrgEventCount === 1 ? 'event' : 'events'} they are currently in. Personal task assignments and event chat access will be removed for those events, while department-assigned tasks will stay.`
      : `Remove ${displayName} from your organization? They are not currently in any organization events, but any active event assignment owned by this organization would also be removed.`
    : '';

  useEffect(() => {
    if (!open) {
      setShowDeptDropdown(false);
      return;
    }
    if (!volunteer) return;
    const deptStr =
      volunteer.departments?.join(', ') ?? (volunteer as { department?: string | null }).department ?? '';
    setSelectedDepartments(deptStr ? deptStr.split(',').map((d: string) => d.trim()).filter(Boolean) : []);
    // Seed the selection when a different volunteer is opened, not each time the parent
    // refreshes the same volunteer's row (that would discard unsaved edits).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, volunteer?.id]);

  const [showAddHours, setShowAddHours] = useState(false);
  const [addMinutes, setAddMinutes] = useState(60);
  const [addHoursDesc, setAddHoursDesc] = useState('');
  const [addingHours, setAddingHours] = useState(false);
  const [showRemoveFromOrganizationConfirm, setShowRemoveFromOrganizationConfirm] = useState(false);
  const [removingFromOrganization, setRemovingFromOrganization] = useState(false);

  const handleAddHours = useCallback(async () => {
    if (!volunteerId || !volunteerUserId || !eventId || validateDuration(addMinutes) !== null) return;
    setAddingHours(true);
    try {
      const newHours = addToTotal(currentHours, addMinutes);
      const { error: updateErr } = await supabase.from('volunteers').update({ hours: newHours }).eq('id', volunteerId);
      if (updateErr) { onError?.(updateErr.message || 'Failed to add hours'); setAddingHours(false); return; }
      const start = new Date();
      const end = new Date(start.getTime() + addMinutes * 60_000);
      const { error: insertErr } = await supabase.from('accepted_volunteer_hours').insert({
        volunteer_id: volunteerUserId,
        event_id: eventId,
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        hours: minutesToHours(addMinutes),
        description: addHoursDesc || null,
        original_activity_id: null,
      });
      if (insertErr) {
        onError?.(insertErr.message || 'Failed to record hours');
        await supabase.from('volunteers').update({ hours: currentHours }).eq('id', volunteerId);
        setAddingHours(false);
        return;
      }
      setShowAddHours(false);
      setAddMinutes(60);
      setAddHoursDesc('');
      onSaved();
    } catch (e) {
      onError?.('Failed to add hours');
    } finally {
      setAddingHours(false);
    }
  }, [volunteerId, volunteerUserId, eventId, currentHours, addMinutes, addHoursDesc, onSaved, onError]);

  const [showDeleteHours, setShowDeleteHours] = useState(false);
  const [removeMinutes, setRemoveMinutes] = useState(0);
  const [deletingHours, setDeletingHours] = useState(false);

  const openOrgActivityPreview = useCallback((activityEvent?: ProfileEvent) => {
    if (!activityEvent) return;
    setPreviewEvent(activityEvent);
    setShowEventPreview(true);
  }, []);

  const handleDeleteHours = useCallback(async () => {
    if (!volunteerId || validateDuration(removeMinutes, { max: currentMinutes }) !== null) return;
    setDeletingHours(true);
    try {
      const newHours = addToTotal(currentHours, -removeMinutes);
      const { error } = await supabase.from('volunteers').update({ hours: newHours }).eq('id', volunteerId);
      if (error) { onError?.(error.message || 'Failed to delete hours'); setDeletingHours(false); return; }
      setShowDeleteHours(false);
      setRemoveMinutes(0);
      onSaved();
    } catch {
      onError?.('Failed to delete hours');
    } finally {
      setDeletingHours(false);
    }
  }, [volunteerId, currentHours, currentMinutes, removeMinutes, onSaved, onError]);

  const handleRemoveFromOrganization = useCallback(async () => {
    if (!orgMemberManage || removingFromOrganization) return;
    setShowRemoveFromOrganizationConfirm(false);
    setRemovingFromOrganization(true);
    try {
      if (orgMemberManage.orgEventIds.length > 0) {
        const { data: volunteerRows, error: volunteerRowsError } = await supabase
          .from('volunteers')
          .select('id, event_id')
          .eq('volunteer_id', orgMemberManage.volunteerUserId)
          .in('event_id', orgMemberManage.orgEventIds)
          .eq('is_active', true);

        if (volunteerRowsError) {
          onError?.(volunteerRowsError.message || 'Failed to remove member from organization events');
          return;
        }

        for (const row of volunteerRows ?? []) {
          if (!row?.id || !row?.event_id) continue;
          await removeVolunteerEventMembership({
            volunteerId: row.id,
            volunteerUserId: orgMemberManage.volunteerUserId,
            eventId: row.event_id,
          });
        }
      }

      const { error } = await supabase
        .from('organization_memberships')
        .update({
          status: 'rejected',
          role: 'member',
          updated_at: new Date().toISOString(),
        })
        .eq('id', orgMemberManage.membershipId)
        .eq('organization_id', orgMemberManage.organizationId);
      if (error) {
        onError?.(error.message || 'Failed to remove member');
        return;
      }
      onSaved();
      onClose();
    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'Failed to remove member');
    } finally {
      setRemovingFromOrganization(false);
    }
  }, [orgMemberManage, removingFromOrganization, onSaved, onClose, onError]);

  const handleRemoveVolunteer = useCallback(async () => {
    if (!volunteerId || !volunteerUserId || !eventId) return;
    if (!window.confirm(`Remove this volunteer from the event?`)) return;
    try {
      await removeVolunteerEventMembership({ volunteerId, volunteerUserId, eventId });
      onSaved();
      onClose();
    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'Failed to remove volunteer');
    }
  }, [volunteerId, volunteerUserId, eventId, onSaved, onClose, onError]);

  const [memberRole, setMemberRole] = useState<'member' | 'admin'>(
    orgMemberManage?.memberRole ?? 'member'
  );
  const [togglingRole, setTogglingRole] = useState(false);

  useEffect(() => {
    if (open) setMemberRole(orgMemberManage?.memberRole ?? 'member');
  }, [open, orgMemberManage?.memberRole]);

  const handleToggleAdminRole = useCallback(async () => {
    if (!orgMemberManage) return;
    const nextRole = memberRole === 'admin' ? 'member' : 'admin';
    setTogglingRole(true);
    try {
      const { error } = await supabase
        .from('organization_memberships')
        .update({ role: nextRole })
        .eq('id', orgMemberManage.membershipId)
        .eq('organization_id', orgMemberManage.organizationId);
      if (error) {
        onError?.(error.message || 'Failed to update role');
        return;
      }
      setMemberRole(nextRole);
      if (nextRole === 'admin') {
        const { data: orgProf } = await supabase
          .from('profiles')
          .select('first_name')
          .eq('id', orgMemberManage.organizationId)
          .maybeSingle();
        const orgName = (orgProf as { first_name?: string | null } | null)?.first_name?.trim() || 'the organization';
        insertVolunteerNotification({
          user_id: orgMemberManage.volunteerUserId,
          title: 'Organization admin',
          message: `You've been promoted to admin for ${orgName}.`,
          type: 'organization_promoted_admin',
          data: {
            organization_id: orgMemberManage.organizationId,
            organization_name: orgName,
          },
          read: false,
        });
      }
      onSaved();
    } catch {
      onError?.('Failed to update role');
    } finally {
      setTogglingRole(false);
    }
  }, [orgMemberManage, memberRole, onSaved, onError]);

  const [certificates, setCertificates] = useState<CertificateItem[]>([]);
  const [uploadingCertificate, setUploadingCertificate] = useState(false);
  const certificateFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open || !volunteerUserId) {
      setCertificates([]);
      return;
    }
    let cancelled = false;
    (async () => {
      let query = supabase
        .from('certificates')
        .select('id, title, image_url, date')
        .eq('user_id', volunteerUserId);
      if (isOrgMemberManage) {
        if (orgMemberManage?.orgEventIds?.length) {
          query = query.in('event_id', orgMemberManage.orgEventIds);
        } else {
          setCertificates([]);
          return;
        }
      } else if (eventId) {
        query = query.eq('event_id', eventId);
      } else {
        setCertificates([]);
        return;
      }
      const { data } = await query.order('created_at', { ascending: false });
      if (!cancelled && data) setCertificates(data as CertificateItem[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, volunteerUserId, eventId, isOrgMemberManage, orgMemberManage?.orgEventIds, orgMemberManage?.membershipId]);

  const handleCertificateFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf' && file.size <= MAX_CERTIFICATE_BYTES) {
      handleUploadCertificate(file);
    } else if (file) {
      if (file.type !== 'application/pdf') onError?.('Only PDF files are allowed');
      else onError?.(`File size must be less than ${MAX_CERTIFICATE_LABEL}`);
    }
    e.target.value = '';
  };

  const handleUploadCertificate = useCallback(
    async (file: File) => {
      if (!volunteerUserId || !eventId || !event) return;
      if (isOrgMemberManage && !orgMemberManage?.hasVolunteerAssignments) {
        onError?.('Add this member to an event from the Events tab before uploading a certificate.');
        return;
      }
      setUploadingCertificate(true);
      try {
        const path = `${volunteerUserId}/certificate_${Date.now()}.pdf`;
        const publicUrl = await uploadImageToStorage(file, 'volunteer-certificates', path);
        const isoDate = new Date().toISOString().split('T')[0];
        const { error } = await supabase.from('certificates').insert({
          user_id: volunteerUserId,
          event_id: eventId,
          title: `Certificate for ${event.name}`,
          organization: event.name || 'Organization',
          date: isoDate,
          image_url: publicUrl,
          is_public: false,
        });
        if (error) { onError?.(error.message || 'Failed to save certificate'); return; }
        onSaved();
        let reload = supabase
          .from('certificates')
          .select('id, title, image_url, date')
          .eq('user_id', volunteerUserId);
        if (isOrgMemberManage && orgMemberManage?.orgEventIds?.length) {
          reload = reload.in('event_id', orgMemberManage.orgEventIds);
        } else {
          reload = reload.eq('event_id', eventId);
        }
        const { data } = await reload.order('created_at', { ascending: false });
        if (data) setCertificates(data as CertificateItem[]);
      } catch (e) {
        onError?.((e instanceof Error && e.message) || 'Failed to upload certificate');
      } finally {
        setUploadingCertificate(false);
      }
    },
    [volunteerUserId, eventId, event, onSaved, onError, isOrgMemberManage, orgMemberManage?.orgEventIds, orgMemberManage?.hasVolunteerAssignments],
  );

  const handleDeleteCertificate = useCallback(
    async (certId: string, imageUrl: string) => {
      if (!window.confirm('Delete this certificate?')) return;
      try {
        const { error } = await supabase.from('certificates').delete().eq('id', certId);
        if (error) { onError?.(error.message || 'Failed to delete certificate'); return; }
        const parts = imageUrl.split('/volunteer-certificates/');
        if (parts.length > 1) await supabase.storage.from('volunteer-certificates').remove([parts[1]]);
        setCertificates((prev) => prev.filter((c) => c.id !== certId));
        onSaved();
      } catch (e) {
        onError?.((e instanceof Error && e.message) || 'Failed to delete certificate');
      }
    },
    [onError, onSaved],
  );

  const handleDownloadCertificate = useCallback(
    async (cert: CertificateItem) => {
      try {
        await saveRemoteFile(cert.image_url, { title: cert.title });
      } catch {
        onError?.('Failed to download certificate');
      }
    },
    [onError],
  );

  const renderMoveDepartmentOption = (
    label: string,
    value: string | null,
    color?: string,
    isLast = false,
  ) => {
    const isCurrent = value
      ? effectiveCurrentDepartment === value
      : effectiveCurrentDepartment === null;
    return (
      <Box
        key={value ?? '__unassigned__'}
        as="button"
        w="100%"
        py="2.5"
        px="4"
        textAlign="left"
        display="flex"
        alignItems="center"
        gap="2.5"
        bg={isCurrent ? 'volyx.bg' : 'transparent'}
        border="none"
        borderBottom={isLast ? 'none' : '1px solid'}
        borderColor="volyx.borderLight"
        cursor={isCurrent || movingDepartment ? 'default' : 'pointer'}
        fontSize="sm"
        opacity={movingDepartment && !isCurrent ? 0.6 : 1}
        pointerEvents={isCurrent || movingDepartment ? 'none' : 'auto'}
        _hover={isCurrent || movingDepartment ? undefined : { bg: 'volyx.bg' }}
        onClick={() => void handleMoveToDepartment(value)}
      >
        <Box
          w="10px"
          h="10px"
          borderRadius="full"
          bg={color ?? 'volyx.textSecondary'}
          flexShrink={0}
        />
        <Text flex="1" m="0" color="volyx.primary" truncate>
          {label}
        </Text>
        {isCurrent && (
          <Check
            size={14}
            style={{
              color: 'var(--chakra-colors-volyx-action)',
              flexShrink: 0,
            }}
          />
        )}
      </Box>
    );
  };

  if (!open) return null;

  return (
    <Box
      position="fixed"
      inset="0"
      bg="rgba(0,0,0,0.5)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      zIndex={1000}
      onClick={onClose}
    >
      <Box
        bg="volyx.bg"
        borderRadius="2xl"
        w="90%"
        maxW="480px"
        maxH="88vh"
        display="flex"
        flexDirection="column"
        boxShadow="0 16px 56px rgba(0,0,0,0.18)"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <Flex
          px="5"
          py="4"
          justifyContent="space-between"
          alignItems="center"
          borderBottom="1px solid"
          borderColor="volyx.borderLight"
        >
          <Text fontSize="lg" fontWeight="bold" color="volyx.primary" m="0">Manage Volunteer</Text>
          <Box
            as="button"
            bg="volyx.lightGray"
            border="none"
            cursor="pointer"
            w="32px"
            h="32px"
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{ bg: 'volyx.mediumGray' }}
            onClick={onClose}
          >
            <X size={16} style={{ color: 'var(--chakra-colors-volyx-textSecondary)' }} />
          </Box>
        </Flex>

        {/* Body */}
        <Box flex="1" overflowY="auto" p="5" display="flex" flexDirection="column" gap="5">

          {/* Volunteer info */}
          <Flex alignItems="center" gap="3">
            <Box
              w="48px"
              h="48px"
              borderRadius="full"
              bg="volyx.primaryLight"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              overflow="hidden"
            >
              {profile?.profile_image ? (
                <img src={profile.profile_image} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Text fontSize="md" fontWeight="bold" color="volyx.primary" m="0">{initials}</Text>
              )}
            </Box>
            <Box>
              <Text fontSize="md" fontWeight="bold" color="volyx.primary" m="0">{displayName}</Text>
              {isOrgMemberManage ? (
                <Text fontSize="xs" color="volyx.textSecondary" m="0">
                  Organization member
                </Text>
              ) : (
                event && <Text fontSize="xs" color="volyx.textSecondary" m="0">{event.name}</Text>
              )}
            </Box>
          </Flex>

          {/* Events in (org members only) */}
          {isOrgMemberManage && orgMemberManage && (
            <Box>
              <SectionLabel>Latest Activity</SectionLabel>
              <Box bg="volyx.lightGray" borderRadius="xl" p="3" border="1px solid" borderColor="volyx.borderLight">
                {orgMemberManage.activities.length === 0 ? (
                  <Text fontSize="sm" color="volyx.textSecondary" m="0">
                    No activity in your organization yet.
                  </Text>
                ) : (
                  <Flex
                    overflowX="auto"
                    overflowY="hidden"
                    py="1"
                    css={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
                  >
                    {orgMemberManage.activities.map((activity) => (
                      <Box
                        as="button"
                        key={activity.id}
                        mr="3"
                        minW="160px"
                        maxW="160px"
                        borderRadius="xl"
                        overflow="hidden"
                        bg="white"
                        boxShadow="0 4px 12px rgba(0,0,0,0.08)"
                        border="1px solid"
                        borderColor="volyx.borderLight"
                        cursor="pointer"
                        flexShrink={0}
                        p="0"
                        textAlign="left"
                        _hover={{ boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
                        onClick={() => openOrgActivityPreview(activity.events)}
                      >
                        <EventImage
                          stored={activity.image}
                          alt={activity.title}
                          w="full"
                          h="85px"
                          objectFit="cover"
                        />
                        <Box p="3">
                          <Text
                            as="h3"
                            m="0"
                            mb="1"
                            fontSize="sm"
                            fontWeight="bold"
                            color="volyx.text"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                          >
                            {activity.title}
                          </Text>
                          <Text
                            as="p"
                            m="0"
                            fontSize="11px"
                            color="volyx.textSecondary"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                          >
                            {activity.department}
                          </Text>
                          <Flex justify="space-between" align="center" mt="2">
                            <Text as="span" fontSize="11px" color="volyx.textSecondary" m="0">
                              {activity.date}
                            </Text>
                            <Text as="span" fontSize="11px" fontWeight="semibold" color="volyx.text" m="0">
                              {formatDuration(activity.hours)}
                            </Text>
                          </Flex>
                        </Box>
                      </Box>
                    ))}
                  </Flex>
                )}
              </Box>
            </Box>
          )}

          {/* Admin Access (org members only) */}
          {isOrgMemberManage && orgMemberManage && (
            <Box>
              <SectionLabel>Admin Access</SectionLabel>
              <Box
                bg="volyx.lightGray"
                borderRadius="xl"
                p="4"
                border="1px solid"
                borderColor={memberRole === 'admin' ? 'blue.200' : 'volyx.borderLight'}
              >
                <Flex align="center" gap="3" mb="3">
                  <Box
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    bg={memberRole === 'admin' ? '#007AFF' : 'volyx.mediumGray'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                    transition="background 0.2s"
                  >
                    {memberRole === 'admin' ? (
                      <ShieldCheck size={18} color="white" />
                    ) : (
                      <ShieldOff size={18} color="white" />
                    )}
                  </Box>
                  <Box minW="0" flex="1">
                    <Text m="0" fontSize="sm" fontWeight="semibold" color="volyx.primary">
                      {memberRole === 'admin' ? 'Admin' : 'Member'}
                    </Text>
                    <Text m="0" fontSize="xs" color="volyx.textSecondary">
                      {memberRole === 'admin'
                        ? 'Can create & manage events and volunteers'
                        : 'Standard member — no admin permissions'}
                    </Text>
                  </Box>
                </Flex>
                <Button
                  w="full"
                  h="36px"
                  fontSize="xs"
                  fontWeight="semibold"
                  borderRadius="lg"
                  border="1px solid"
                  bg={memberRole === 'admin' ? 'transparent' : '#007AFF'}
                  color={memberRole === 'admin' ? 'volyx.error' : 'white'}
                  borderColor={memberRole === 'admin' ? 'volyx.error' : '#007AFF'}
                  _hover={{ opacity: 0.85 }}
                  disabled={togglingRole}
                  onClick={handleToggleAdminRole}
                >
                  {togglingRole
                    ? '…'
                    : memberRole === 'admin'
                      ? 'Revoke Admin Access'
                      : 'Grant Admin Access'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Department + Shifts (event context only) */}
          {!isOrgMemberManage && (
          <>
          <Box>
            <SectionLabel>Department</SectionLabel>
            <Box position="relative">
              <Box
                as="button"
                w="100%"
                h="48px"
                px="4"
                textAlign="left"
                bg="volyx.lightGray"
                borderRadius="xl"
                border="1px solid"
                borderColor="volyx.borderLight"
                fontSize="sm"
                color={effectiveCurrentDepartment ? 'volyx.primary' : 'volyx.textSecondary'}
                cursor={movingDepartment ? 'default' : 'pointer'}
                opacity={movingDepartment ? 0.7 : 1}
                pointerEvents={movingDepartment ? 'none' : 'auto'}
                onClick={() => setShowDeptDropdown((d) => !d)}
              >
                <Flex justifyContent="space-between" alignItems="center">
                  <Text truncate m="0">
                    {movingDepartment
                      ? 'Updating…'
                      : effectiveCurrentDepartment ?? 'Unassigned'}
                  </Text>
                  <ChevronDown
                    size={16}
                    style={{
                      color: 'var(--chakra-colors-volyx-textSecondary)',
                      transform: showDeptDropdown ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s',
                      flexShrink: 0,
                    }}
                  />
                </Flex>
              </Box>
              {showDeptDropdown && (
                <Box
                  position="absolute"
                  top="100%"
                  left="0"
                  right="0"
                  mt="1"
                  bg="volyx.bg"
                  borderRadius="xl"
                  boxShadow="0 8px 24px rgba(0,0,0,0.12)"
                  zIndex={10}
                  maxH="220px"
                  overflowY="auto"
                  border="1px solid"
                  borderColor="volyx.borderLight"
                  overflow="hidden"
                >
                  {onUpdateVolunteerDepartment ? (
                    <>
                      {renderMoveDepartmentOption('Unassigned', null, undefined, moveDepartmentOptions.length === 0)}
                      {moveDepartmentOptions.length > 0 ? (
                        moveDepartmentOptions.map((dept, index) =>
                          renderMoveDepartmentOption(
                            dept,
                            dept,
                            departmentColors?.[dept],
                            index === moveDepartmentOptions.length - 1,
                          ),
                        )
                      ) : (
                        <Box py="3" px="4" fontSize="sm" color="volyx.textSecondary" fontStyle="italic">
                          No departments for this event
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box py="3" px="4" fontSize="sm" color="volyx.textSecondary" fontStyle="italic">
                      No departments for this event
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Shifts */}
          {eventShifts.length > 0 && (
            <Box>
              <SectionLabel>Shifts</SectionLabel>
              <ShiftList
                shifts={eventShifts}
                selectedIds={assignedShiftIds}
                onToggle={(id) => void handleShiftToggle(id)}
                blockFull
                disabled={updatingShift}
              />
            </Box>
          )}
          </>
          )}

          {/* Hours */}
          <Box>
            <SectionLabel>Hours</SectionLabel>
            <Box bg="volyx.lightGray" borderRadius="xl" p="4">
              <Flex alignItems="center" gap="3" mb="3">
                <Box
                  w="40px"
                  h="40px"
                  borderRadius="full"
                  bg="volyx.action"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Clock size={18} color="white" />
                </Box>
                <Box minW={0}>
                  {isOrgMemberManage ? (
                    <Text fontSize="xl" fontWeight="bold" color="volyx.primary" m="0" lineHeight="1.2">
                      All Events: {formatDuration(totalHoursAllOrgEvents)}
                    </Text>
                  ) : (
                    <>
                      <Text fontSize="xl" fontWeight="bold" color="volyx.primary" m="0" lineHeight="1.2">
                        {formatDuration(currentHours)}
                      </Text>
                      <Text fontSize="xs" color="volyx.textSecondary" m="0" mt="1">
                        Total hours
                      </Text>
                    </>
                  )}
                </Box>
              </Flex>
              <Flex gap="2">
                <Button
                  flex="1"
                  h="36px"
                  bg="volyx.action"
                  color="white"
                  borderRadius="lg"
                  fontWeight="semibold"
                  fontSize="xs"
                  border="none"
                  _hover={{ opacity: 0.9 }}
                  disabled={!volunteerId}
                  onClick={() => setShowAddHours(true)}
                >
                  <Plus size={13} style={{ marginRight: 4 }} />
                  Add Hours
                </Button>
                <Button
                  flex="1"
                  h="36px"
                  bg="transparent"
                  border="1px solid"
                  borderColor="volyx.error"
                  color="volyx.error"
                  borderRadius="lg"
                  fontWeight="semibold"
                  fontSize="xs"
                  _hover={{ bg: '#FFF5F5' }}
                  disabled={!volunteerId || currentMinutes < MINUTE_STEP}
                  onClick={() => {
                    setShowDeleteHours(true);
                    setRemoveMinutes(Math.min(60, Math.floor(currentMinutes / MINUTE_STEP) * MINUTE_STEP));
                  }}
                >
                  <Minus size={13} style={{ marginRight: 4 }} />
                  Remove Hours
                </Button>
              </Flex>
            </Box>
          </Box>

          {/* Certificate */}
          <Box>
            <SectionLabel>Certificate</SectionLabel>
            <input
              ref={certificateFileRef}
              type="file"
              accept="application/pdf"
              style={{ display: 'none' }}
              onChange={handleCertificateFileChange}
            />
            <Button
              w="full"
              h="44px"
              bg="volyx.lightGray"
              color="volyx.primary"
              borderRadius="xl"
              fontWeight="semibold"
              fontSize="sm"
              border="1px dashed"
              borderColor="volyx.mediumGray"
              _hover={{ bg: 'volyx.mediumGray' }}
              onClick={() => certificateFileRef.current?.click()}
              disabled={
                uploadingCertificate ||
                (isOrgMemberManage && !orgMemberManage?.hasVolunteerAssignments)
              }
            >
              <FileUp size={15} style={{ marginRight: 6 }} />
              {uploadingCertificate ? 'Uploading...' : 'Upload PDF (max 5MB)'}
            </Button>

            {certificates.length > 0 && (
              <Box mt="3" display="flex" flexDirection="column" gap="2">
                {certificates.map((cert) => (
                  <Flex
                    key={cert.id}
                    alignItems="center"
                    justifyContent="space-between"
                    py="2.5"
                    px="3"
                    bg="volyx.lightGray"
                    borderRadius="lg"
                  >
                    <Box minW="0" flex="1">
                      <Text fontSize="sm" fontWeight="medium" color="volyx.primary" truncate m="0">{cert.title}</Text>
                      <Text fontSize="xs" color="volyx.textSecondary" m="0">{cert.date}</Text>
                    </Box>
                    <Flex gap="1" alignItems="center" flexShrink={0}>
                      <Box
                        as="button"
                        w="30px"
                        h="30px"
                        borderRadius="lg"
                        bg="transparent"
                        border="none"
                        cursor="pointer"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        _hover={{ bg: 'volyx.mediumGray' }}
                        onClick={() => { void handleDownloadCertificate(cert); }}
                      >
                        <Download size={14} style={{ color: 'var(--chakra-colors-volyx-primary)' }} />
                      </Box>
                      <Box
                        as="button"
                        w="30px"
                        h="30px"
                        borderRadius="lg"
                        bg="transparent"
                        border="none"
                        cursor="pointer"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        _hover={{ bg: '#FFF5F5' }}
                        onClick={() => handleDeleteCertificate(cert.id, cert.image_url)}
                      >
                        <Trash2 size={14} style={{ color: 'var(--chakra-colors-volyx-error)' }} />
                      </Box>
                    </Flex>
                  </Flex>
                ))}
              </Box>
            )}
          </Box>

          {/* Remove Volunteer / Remove from organization */}
          <Button
            w="100%"
            h="44px"
            bg="transparent"
            border="1px solid"
            borderColor="volyx.error"
            color="volyx.error"
            borderRadius="xl"
            fontWeight="semibold"
            fontSize="sm"
            _hover={{ bg: '#FFF5F5' }}
            disabled={removingFromOrganization}
            onClick={isOrgMemberManage ? () => setShowRemoveFromOrganizationConfirm(true) : handleRemoveVolunteer}
          >
            {isOrgMemberManage
              ? removingFromOrganization
                ? 'Removing...'
                : 'Remove from organization'
              : 'Remove Volunteer'}
          </Button>
        </Box>
      </Box>

      {/* Add Hours sub-modal */}
      {showAddHours && (
        <Box
          position="fixed"
          inset="0"
          bg="rgba(0,0,0,0.5)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={1001}
          onClick={() => !addingHours && setShowAddHours(false)}
        >
          <Box
            bg="volyx.bg"
            borderRadius="2xl"
            w="90%"
            maxW="380px"
            boxShadow="0 16px 56px rgba(0,0,0,0.18)"
            onClick={(e) => e.stopPropagation()}
          >
            <Flex px="5" py="4" justifyContent="space-between" alignItems="center" borderBottom="1px solid" borderColor="volyx.borderLight">
              <Text fontSize="lg" fontWeight="bold" color="volyx.primary" m="0">Add Hours</Text>
              <Box
                as="button"
                bg="volyx.lightGray"
                border="none"
                cursor="pointer"
                w="32px"
                h="32px"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: 'volyx.mediumGray' }}
                onClick={() => setShowAddHours(false)}
              >
                <X size={16} style={{ color: 'var(--chakra-colors-volyx-textSecondary)' }} />
              </Box>
            </Flex>
            <Box p="5">
              <Box mb="4">
                <DurationStepper value={addMinutes} onChange={setAddMinutes} />
              </Box>
              <Box mb="4">
                <Text fontSize="xs" color="volyx.textSecondary" mb="1">Description (optional)</Text>
                <textarea
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '10px 12px',
                    borderRadius: '12px',
                    border: '1px solid var(--chakra-colors-volyx-borderLight)',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    background: 'var(--chakra-colors-volyx-lightGray)',
                    outline: 'none',
                    resize: 'vertical',
                    color: 'var(--chakra-colors-volyx-primary)',
                  }}
                  value={addHoursDesc}
                  onChange={(e) => setAddHoursDesc(e.target.value)}
                  placeholder="What did the volunteer do?"
                />
              </Box>
              <Flex gap="2">
                <Button flex={1} h="44px" bg="volyx.lightGray" color="volyx.primary" borderRadius="xl" onClick={() => setShowAddHours(false)} disabled={addingHours}>Cancel</Button>
                <Button flex={1} h="44px" bg="volyx.action" color="white" borderRadius="xl" onClick={handleAddHours} disabled={addingHours}>{addingHours ? 'Adding...' : 'Add Hours'}</Button>
              </Flex>
            </Box>
          </Box>
        </Box>
      )}

      {/* Delete Hours sub-modal */}
      {showDeleteHours && (
        <Box
          position="fixed"
          inset="0"
          bg="rgba(0,0,0,0.5)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={1001}
          onClick={() => !deletingHours && setShowDeleteHours(false)}
        >
          <Box
            bg="volyx.bg"
            borderRadius="2xl"
            w="90%"
            maxW="380px"
            boxShadow="0 16px 56px rgba(0,0,0,0.18)"
            onClick={(e) => e.stopPropagation()}
          >
            <Flex px="5" py="4" justifyContent="space-between" alignItems="center" borderBottom="1px solid" borderColor="volyx.borderLight">
              <Text fontSize="lg" fontWeight="bold" color="volyx.primary" m="0">Remove Hours</Text>
              <Box
                as="button"
                bg="volyx.lightGray"
                border="none"
                cursor="pointer"
                w="32px"
                h="32px"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: 'volyx.mediumGray' }}
                onClick={() => setShowDeleteHours(false)}
              >
                <X size={16} style={{ color: 'var(--chakra-colors-volyx-textSecondary)' }} />
              </Box>
            </Flex>
            <Box p="5">
              <Box mb="2">
                <DurationStepper value={removeMinutes} onChange={setRemoveMinutes} max={currentMinutes} tone="error" />
              </Box>
              <Text fontSize="xs" color="volyx.textSecondary" textAlign="center" mb="4">Max: {formatDuration(currentHours)}</Text>
              <Flex gap="2">
                <Button flex={1} h="44px" bg="volyx.lightGray" color="volyx.primary" borderRadius="xl" onClick={() => setShowDeleteHours(false)} disabled={deletingHours}>Cancel</Button>
                <Button flex={1} h="44px" bg="volyx.error" color="white" borderRadius="xl" onClick={handleDeleteHours} disabled={deletingHours || validateDuration(removeMinutes, { max: currentMinutes }) !== null}>{deletingHours ? 'Removing...' : 'Remove Hours'}</Button>
              </Flex>
            </Box>
          </Box>
        </Box>
      )}
      <EventPreviewModal
        visible={showEventPreview}
        onClose={() => setShowEventPreview(false)}
        isOrganization={false}
        hideOrganizationInfo={false}
        event={previewEvent}
      />
      <ConfirmModal
        isOpen={showRemoveFromOrganizationConfirm}
        title="Remove member?"
        message={removeFromOrganizationMessage}
        confirmText="Remove member"
        cancelText="Cancel"
        variant="danger"
        onCancel={() => setShowRemoveFromOrganizationConfirm(false)}
        onConfirm={() => void handleRemoveFromOrganization()}
      />
    </Box>
  );
}
